import { useTranslate, HttpError } from '@refinedev/core';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Select } from 'antd';
import type {
  BannerActionDetailOut,
  BannerActionIn,
} from '@/api/generated/schemas';
import {
  BannerSize,
  BannerActionKind,
  ImageKind,
} from '@/api/generated/schemas';
import { ImageUploadField } from '@/components/common/ImageUploadField';
import { RATIO, ratioLabel } from '@/components/user_catalog/creative/creative';
import { BannerActionTargetField } from '@/components/user_catalog/banner_actions/BannerActionTargetField';
import { targetAwareErrorNotification } from '@/components/user_catalog/banner_actions/bannerActionTarget';

export const BannerActionCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, form } = useForm<
    BannerActionDetailOut,
    HttpError,
    BannerActionIn
  >({
    resource: 'user_catalog.banner_actions',
    errorNotification: targetAwareErrorNotification,
  });

  const kind = Form.useWatch('kind', form);

  /** Crop to the shape the app will render, so upload loses nothing. */
  const size = Form.useWatch('size', form);
  const aspect = size ? RATIO[size].w / RATIO[size].h : undefined;

  /**
   * An already-uploaded image was cropped to the old size's ratio, so it
   * cannot survive a size change: drop it and require a fresh upload.
   */
  const clearImage = () => form?.setFieldsValue({ image_storage_key: null });

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item
          name="name"
          label={t('user_catalog.banner_actions.fields.name')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="size"
          label={t('user_catalog.banner_actions.fields.size')}
        >
          <Select
            onChange={clearImage}
            options={[
              {
                label: t('user_catalog.banner_actions.size.large'),
                value: BannerSize.large,
              },
              {
                label: t('user_catalog.banner_actions.size.small'),
                value: BannerSize.small,
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="kind"
          label={t('user_catalog.banner_actions.fields.kind')}
          extra={
            kind === BannerActionKind.user_profile
              ? t('user_catalog.banner_actions.hints.user_profile_no_route')
              : undefined
          }
        >
          <Select
            onChange={() => form?.setFieldsValue({ target_ref: null })}
            options={Object.values(BannerActionKind).map((value) => ({
              label: t(`user_catalog.banner_actions.kind.${value}`),
              value,
            }))}
          />
        </Form.Item>

        <BannerActionTargetField kind={kind} />

        <Form.Item name="image_storage_key" hidden>
          <Input />
        </Form.Item>
        <ImageUploadField
          key={size}
          imageKind={ImageKind.banners}
          onUpload={(key) => form?.setFieldsValue({ image_storage_key: key })}
          label={
            size
              ? `${t('user_catalog.banner_actions.fields.image_url')} (${ratioLabel(size)})`
              : t('user_catalog.banner_actions.fields.image_url')
          }
          aspect={aspect}
        />
      </Form>
    </Create>
  );
};
